// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Dynamic asset icon component that uses the organization's selected asset icon
 * with fallback to PrecisionManufacturing if no icon is configured or invalid.
 */
import { MARKER_CATEGORIES } from '@/account/constants/asset-marker-categories';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PrecisionManufacturingRounded } from '@/constants/hardfin-standard-icons';
import { SvgIconProps } from '@mui/material';

// Create flat map of all available asset icons from categories
const AVAILABLE_ASSET_ICONS = Object.values(MARKER_CATEGORIES).reduce(
  (acc, categoryIcons) => ({ ...acc, ...categoryIcons }),
  {}
) as Record<string, React.ComponentType<SvgIconProps>>;

interface Props {
  /**
   * Props to pass to the underlying MUI icon component.
   */
  iconProps?: SvgIconProps;
}

/**
 * Renders the organization's configured asset marker with fallback to PrecisionManufacturing.
 * Validates that the configured icon exists in the available icon set before rendering.
 */
export function OrganizationDefaultAssetMarker(props: Props) {
  const { user } = useCurrentUser();

  const assetMarkerName = user?.organization?.assetMarker;

  if (!assetMarkerName) {
    return <PrecisionManufacturingRounded {...props.iconProps} />;
  }

  const IconComponent = AVAILABLE_ASSET_ICONS[assetMarkerName];

  if (!IconComponent) {
    return <PrecisionManufacturingRounded {...props.iconProps} />;
  }

  return <IconComponent {...props.iconProps} />;
}
